import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import DevicesIcon from '@mui/icons-material/Devices';
import ContactsIcon from '@mui/icons-material/Contacts';

interface Business {
  id: string;
  name: string;
  abn: string;
  deviceCount: number;
  contactCount: number;
  address?: {
    street: string;
    city: string;
    state: string;
    postCode: string;
  };
}

const UserBusinesses: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchBusinesses();
  }, []);

  const fetchBusinesses = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/businesses', {
        headers: {
          'Authorization': `Bearer ${auth.tokens?.accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch businesses');
      }

      const data = await response.json();
      setBusinesses(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <BusinessIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" component="h1">
              My Business
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your business information, devices, and contacts
            </Typography>
          </Box>
        </Box>
      </Paper>

      {businesses.length === 0 ? (
        <Alert severity="info">
          You are not associated with any businesses yet. Please contact your administrator.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {businesses.map((business) => (
            <Grid item xs={12} key={business.id}>
              <Card>
                <CardContent>
                  <Typography variant="h5" component="h2" gutterBottom>
                    {business.name}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    ABN: {business.abn}
                  </Typography>

                  {business.address && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {business.address.street}, {business.address.city}, {business.address.state} {business.address.postCode}
                    </Typography>
                  )}

                  <Box mt={2} display="flex" gap={1}>
                    <Chip
                      icon={<DevicesIcon />}
                      label={`${business.deviceCount} Devices`}
                      color="primary"
                      variant="outlined"
                    />
                    <Chip
                      icon={<ContactsIcon />}
                      label={`${business.contactCount} Contacts`}
                      color="secondary"
                      variant="outlined"
                    />
                  </Box>
                </CardContent>
                
                <CardActions>
                  <Button
                    size="small"
                    onClick={() => navigate(`/my-business/${business.id}`)}
                  >
                    View Details
                  </Button>
                  <Button
                    size="small"
                    startIcon={<DevicesIcon />}
                    onClick={() => navigate('/devices')}
                  >
                    Devices
                  </Button>
                  <Button
                    size="small"
                    startIcon={<ContactsIcon />}
                    onClick={() => navigate('/contacts')}
                  >
                    Contacts
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default UserBusinesses;
